import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { Text } from "react-native";
import { TextInputProps } from "ui/atoms/input/interfaces";
import { TextInput } from "ui/atoms/input/TextInput";
import { View } from "ui/atoms/view";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";
export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  helperText?: string;
  type?: TextInputProps["type"];
  invalidMessage?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  onSubmitEditing?: () => void;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  textInputClassName?: string;
}

export function FormControlledInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  isInvalid,
  isReadOnly,
  isDisabled,
  isRequired,
  autoFocus,
  placeholder,
  label,
  type = "text",
  invalidMessage,
  helperText,
  control,
  rules,
  onSubmitEditing,
  className,
  prefix,
  postfix,
  textInputClassName,
}: Props<TFieldValues>) {
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error }, field: { value, onChange, name } }) => (
        <FormControl
          isInvalid={isInvalid || !!error}
          size="md"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        >
          {label ? (
            <Text className="test-sm font-medium mb-2 text-gray-600">
              {label}
              {!isRequired ? " (Optional)" : ""}
            </Text>
          ) : null}
          <View className={`relative my-1 fill-white ${className}`}>
            {prefix}
            <TextInput
              id={id}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type={type}
              variant={undefined}
              onSubmitEditing={onSubmitEditing}
              autoFocus={autoFocus}
              className={`truncate ${textInputClassName}`}
            />
            {postfix}
          </View>
          {helperText ? (
            <FormControlHelper>
              <FormControlHelperText className="text-gray-500">
                {helperText ?? " "}
              </FormControlHelperText>
            </FormControlHelper>
          ) : null}
          {invalidMessage || error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error?.message || invalidMessage}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      )}
      rules={localRules}
    />
  );
}
